import React, { useMemo } from "react"
import Modal from "components/common/Modal"
import {
    ButtonV2,
    ControlledBinarySelectorV2,
    ControlledSelectV2,
    StepsV2,
    TagWithIcon,
    TypographyV2
} from "components/v2/index"
import { CloseIcon } from "components/icons"
import { usePublishLibraryTemplateModal } from "./usePublishLibraryTemplateModal"
import { PublishRulesDescription, SelectedExerciseCard, UnitItemDetails } from "./components"
import { classNames } from "utils"
import { getLibraryElementLocal, getLibraryElementPublishTitle } from "./publishLibraryTemplateModalConstants"
import { ElementType, LibraryTemplateType, ExerciseStatuses, UnitChapterElementType } from "@prisma/client"
import { useCareProtocolElementList } from "../../careProtocol/careProtocolElement/CareProtocolElemetList/useCareProtocolElementList"
import { EventTypes } from "types"

const PublishLibraryTemplateModal = () => {
    const {
        t,
        control,
        publishSteps,
        publishOptions,
        selectedExercise,
        replacedExercise,
        isPublishAndReset,
        submitBtnTitle,
        isReviewed,
        mappedExerciseForSelect,
        onClose,
        onBackClick,
        onContinueClick,
        isUnit,
        unitChapters,
        unitElements
    } = usePublishLibraryTemplateModal()
    const { careProtocolElements } = useCareProtocolElementList()

    if (selectedExercise.template_type === LibraryTemplateType.CARE_PROTOCOL && !isReviewed) {
        onContinueClick()
    }

    const unPublishedInnerElements = useMemo(() => {
        const unPublishedElements: any[] = []
        if (selectedExercise.template_type === LibraryTemplateType.CARE_PROTOCOL) {
            careProtocolElements.forEach((element) => {
                if (element?.element_type === ElementType.UNIT) {
                    if (element?.unit_element?.status && element?.unit_element?.status === ExerciseStatuses.INIT) {
                        unPublishedElements.push(element)
                    }
                } else if (element?.element_type === ElementType.EXERCISE) {
                    if (
                        element?.exercise_element?.status &&
                        element?.exercise_element?.status === ExerciseStatuses.INIT
                    ) {
                        unPublishedElements.push(element)
                    }
                } else if (element?.element_type === ElementType.QUESTIONNAIRE) {
                    if (element?.questionnaire_element && !element?.questionnaire_element?.is_published) {
                        unPublishedElements.push(element)
                    }
                }
            })
        }

        if (selectedExercise.template_type === LibraryTemplateType.UNIT) {
            unitElements.forEach((unit_chapter_element) => {
                if (unit_chapter_element?.element_type === ElementType.QUESTIONNAIRE) {
                    if (!unit_chapter_element?.questionnaire?.is_published) {
                        unPublishedElements.push(unit_chapter_element)
                    }
                } else if (
                    [UnitChapterElementType.EXERCISE, UnitChapterElementType.EDUCATIONAL_CONTENT].includes(
                        unit_chapter_element?.element_type
                    )
                ) {
                    if (
                        unit_chapter_element?.library_template?.status &&
                        unit_chapter_element.library_template.status === ExerciseStatuses.INIT
                    ) {
                        unPublishedElements.push(unit_chapter_element)
                    }
                }
            })
        }
        return unPublishedElements
    }, [careProtocolElements])

    const getElement = (element: any) => {
        if (element?.element_type === ElementType.UNIT) {
            return (
                <div className="flex flex-row gap-2 items-center">
                    <TagWithIcon eventType={EventTypes.Unit} />
                    <TypographyV2 width="bold" type="base" className="text-med-black">
                        {element?.unit_element?.title}
                    </TypographyV2>
                </div>
            )
        } else if (element?.element_type === ElementType.EXERCISE) {
            return (
                <div className="flex flex-row gap-2 items-center">
                    <TagWithIcon eventType={EventTypes.Exercise} />
                    <TypographyV2 width="bold" type="base" className="text-med-black">
                        {element?.exercise_element?.title}
                    </TypographyV2>
                </div>
            )
        } else if (element?.element_type === ElementType.QUESTIONNAIRE) {
            return (
                <div className="flex flex-row gap-2 items-center">
                    <TagWithIcon eventType={EventTypes.Questionnaire} />
                    <TypographyV2 width="bold" type="base" className="text-med-black">
                        {element?.questionnaire_element?.title}
                    </TypographyV2>
                </div>
            )
        }
    }

    if (unPublishedInnerElements.length > 0) {
        return (
            <Modal show onClose={onClose} centered cardClassName="w-2/4  max-w-screen-lg">
                <div className="p-4 flex justify-between border-b border-border-blue">
                    <TypographyV2 width="bold" type="base" className="text-med-black">
                        {t("common:allInnerElementsShouldBePublished")}
                    </TypographyV2>
                </div>
                <div className="max-h-72 h-32 overflow-y-auto">
                    <div className="flex flex-col gap-2">
                        {unPublishedInnerElements.map((element) => (
                            <div key={element.id} className="border-b border-border-blue p-6">
                                {getElement(element)}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="p-4 flex justify-between border-t border-border-blue">
                    <ButtonV2 variant="light-gray" onClick={onClose} text={t("common:cancel")} />
                </div>
            </Modal>
        )
    }

    return (
        <Modal show onClose={onClose} centered cardClassName="w-2/4  max-w-screen-lg">
            <div className="p-4 flex justify-between border-b border-border-blue">
                <TypographyV2 width="bold" type="base" className="text-med-black">
                    {t("common:publish")} {getLibraryElementLocal(selectedExercise.template_type)}
                </TypographyV2>
                <div className="flex items-center">
                    <div className="mr-3.5">
                        <StepsV2 viewType="row" steps={publishSteps} currentStep={isReviewed ? 2 : 1} />
                    </div>
                    <CloseIcon onClick={onClose} className="cursor-pointer text-[#9CA3AF]" height={20} width={20} />
                </div>
            </div>
            <div className="p-6 h-72 overflow-y-auto">
                {!isReviewed && (
                    <>
                        <ControlledBinarySelectorV2
                            label={t("select_publish_method")}
                            placeholder={t("select_publish_method")}
                            control={control}
                            name="status"
                            options={publishOptions}
                        />
                        {isPublishAndReset && (
                            <ControlledSelectV2
                                classList={{ wrapper: "mt-6" }}
                                placeholder={getLibraryElementPublishTitle(selectedExercise.template_type)}
                                label={getLibraryElementPublishTitle(selectedExercise.template_type)}
                                options={mappedExerciseForSelect}
                                control={control}
                                name="replacedExerciseId"
                            />
                        )}
                    </>
                )}
                {isReviewed && (
                    <div className="flex flex-col justify-between h-full">
                        <div>
                            <TypographyV2 width="semibold" className="text-gray-600">
                                {t("common:publish")}
                            </TypographyV2>
                            <div className="mt-2 flex gap gap-x-6">
                                <SelectedExerciseCard {...selectedExercise} />
                                {isPublishAndReset && <SelectedExerciseCard {...replacedExercise} />}
                            </div>
                        </div>
                        {isUnit && <UnitItemDetails unitChapters={unitChapters} />}
                        <PublishRulesDescription />
                    </div>
                )}
            </div>
            <div
                className={classNames(
                    "p-4 flex  border-t bg-[#F3F4F6] border-border-blue",
                    isReviewed ? "justify-between" : "justify-end"
                )}
            >
                {isReviewed && (
                    <ButtonV2
                        className="self-start"
                        variant="light-gray"
                        onClick={onBackClick}
                        text={t("common:back")}
                    />
                )}
                <div className="flex gap-2.5 justify-end">
                    <ButtonV2 variant="light-gray" onClick={onClose} text={t("common:cancel")} />
                    <ButtonV2 onClick={onContinueClick} text={submitBtnTitle} />
                </div>
            </div>
        </Modal>
    )
}

export default PublishLibraryTemplateModal
